import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, FolderKanban, BarChart3, Bell, Users2,
  Archive, FileCheck2, ScrollText, Settings, LogOut, Menu, Moon, Sun,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: "*" },
  { to: "/projects", label: "Proyek", icon: FolderKanban, roles: "*" },

  { to: "/reports", label: "Laporan", icon: BarChart3, roles: ["SUPER_ADMIN", "DIREKTUR", "MANAGER_OPERASIONAL"] },
  { to: "/notifications", label: "Notifikasi", icon: Bell, roles: "*" },
  { to: "/users", label: "Pengguna", icon: Users2, roles: ["SUPER_ADMIN"] },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, roles: ["SUPER_ADMIN", "MANAGER_OPERASIONAL", "DIREKTUR"] },
  { to: "/settings", label: "Pengaturan", icon: Settings, roles: ["SUPER_ADMIN"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, []);

  const allowed = (roles) => roles === "*" || roles.includes(user?.role);
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
        <div className="h-9 w-9 rounded-lg bg-white border border-border flex items-center justify-center shrink-0 overflow-hidden">
          <img src="/brand/indoraj-logo.png" alt="Indoraj" className="h-7 w-7 object-contain" />
        </div>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm leading-tight truncate">INDORAJ</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Arsip Multiguna</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-1">
        {NAV.filter((n) => allowed(n.roles)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`nav-${n.to.replace(/\//g, "") || "dashboard"}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{n.label}</span>
            {n.to === "/notifications" && unread > 0 && (
              <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">{unread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-border text-[10px] text-muted-foreground">v1.0 • Enterprise</div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="fixed inset-0 bg-black/50 z-40 lg:hidden" />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "tween", duration: 0.25 }}
              className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border z-50 lg:hidden">
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 sm:px-6 border-b border-border bg-white/70 dark:bg-black/50 backdrop-blur-xl">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">
            {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </Button>
          <Button variant="ghost" size="icon" className="relative" onClick={() => nav("/notifications")} data-testid="notif-bell">
            <Bell className="h-[18px] w-[18px]" />
            {unread > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-accent transition-colors duration-200" data-testid="user-menu">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback></Avatar>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-semibold leading-tight">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{ROLE_LABELS[user?.role]}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn" className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
